import { useEffect, useState } from 'react';
import { Minus, Plus } from 'lucide-react';
import type { CheckInListRow } from '@peles/shared';
import { EntityModal } from '../common/EntityModal';

export interface PartyArrival {
  arrived: number;
  memberIds: string[];
}

/**
 * Who of a party is here: a count for everybody, and a tick for each person
 * the party named. Ticking a name counts them; the count never drops below
 * the names ticked.
 */
export function PartyArrivalModal({
  row,
  onClose,
  onSave,
  saving,
}: {
  row: CheckInListRow | null;
  onClose: () => void;
  onSave: (arrival: PartyArrival) => void;
  saving?: boolean;
}) {
  const [arrived, setArrived] = useState(0);
  const [memberIds, setMemberIds] = useState<string[]>([]);

  useEffect(() => {
    if (!row) return;
    const here = row.members.filter((m) => m.checkedInAt).map((m) => m.id);
    setMemberIds(here);
    setArrived(row.arrivedCount > 0 ? row.arrivedCount : Math.max(row.expected, 1));
  }, [row]);

  if (!row) return null;
  const max = Math.max(row.expected, row.members.length, arrived, 1) + 10;
  const toggle = (id: string) => {
    const next = memberIds.includes(id) ? memberIds.filter((m) => m !== id) : [...memberIds, id];
    setMemberIds(next);
    setArrived((current) => Math.max(current, next.length));
  };

  return (
    <EntityModal
      open
      onClose={onClose}
      title={row.guestName}
      size="xs"
      footer={
        <div className="flex w-full items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => onSave({ arrived: 0, memberIds: [] })}
            disabled={saving || row.arrivedCount === 0}
            className="px-3 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40"
          >
            Nobody yet
          </button>
          <button
            type="button"
            onClick={() => onSave({ arrived, memberIds })}
            disabled={saving}
            className="px-4 py-2 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            Save
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-500">
          {row.tableNumber != null ? `Table ${row.tableNumber} · ` : ''}
          {row.expected === 0 ? 'Said they are not coming' : `${row.expected} expected`}
        </p>
        <div className="flex items-center justify-center gap-5">
          <button
            type="button"
            aria-label="One fewer arrived"
            disabled={arrived <= Math.max(memberIds.length, 0)}
            onClick={() => setArrived((n) => n - 1)}
            className="w-12 h-12 rounded-full border border-gray-300 flex items-center justify-center text-gray-700 disabled:opacity-40"
          >
            <Minus className="w-5 h-5" />
          </button>
          <div className="text-center" aria-live="polite">
            <div className="text-3xl font-bold text-gray-900" data-testid="arrived-count">
              {arrived}
            </div>
            <div className="text-xs text-gray-500">
              {row.expected > 0 ? `of ${row.expected} arrived` : 'arrived'}
            </div>
          </div>
          <button
            type="button"
            aria-label="One more arrived"
            disabled={arrived >= max}
            onClick={() => setArrived((n) => n + 1)}
            className="w-12 h-12 rounded-full border border-gray-300 flex items-center justify-center text-gray-700 disabled:opacity-40"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>
        {row.members.length > 0 && (
          <fieldset>
            <legend className="text-sm font-medium text-gray-900">Who is here</legend>
            <ul className="mt-2 divide-y divide-gray-100">
              {row.members.map((member) => (
                <li key={member.id}>
                  <label className="flex items-center gap-3 py-2.5 min-h-11">
                    <input
                      type="checkbox"
                      checked={memberIds.includes(member.id)}
                      onChange={() => toggle(member.id)}
                      className="w-5 h-5 rounded border-gray-300 text-primary-600"
                    />
                    <span className="flex-1 text-sm text-gray-800">{member.name}</span>
                    {member.attending === false && (
                      <span className="text-xs text-gray-400">said not coming</span>
                    )}
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
        )}
      </div>
    </EntityModal>
  );
}
